import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';


enum RestMethod {
  GET,
  POST,
  PUT,
  DELETE
}

export interface RemoteError {
  code: string;
  description: string;
  value: string;
}

export class RemoteErrors {
  status: number;
  statusText: string;
  errors: RemoteError[];
  sourceException: any;

  constructor (sourceException: any) {
    this.sourceException = sourceException;
    this.errors = [];
    if (sourceException.error) {
      this.errors.push(sourceException.error as RemoteError);
    }
    this.status = sourceException.status ? sourceException.status : null;
    this.statusText = sourceException.statusText ? sourceException.statusText : null;
  }

  toString(): string {
    return this.sourceException ? this.sourceException.toString() : Object.prototype.toString.call(this);
  }
}


@Injectable()
export class RemoteApiService {

  private streamUrl: string = environment.streamUrl;
  private stationUrl: string = environment.stationUrl;
  private profileUrl: string = environment.profileUrl;
  private downloadsUrl: string = environment.downloadsUrl;
  private subscriptionsUrl: string = environment.subscriptionsUrl;
  private audioSubscriptionsUrl: string = environment.audioSubscriptionsUrl;
  private silenceLogsUrl: string = environment.silenceLogsUrl;
  private usersUrl: string = environment.usersUrl;
  private configUrl: string = environment.configUrl;

  constructor (private http: HttpClient) { }


  getUsers<T>(path: string, params?: {}): Observable<T> {
    return this.get(this.profileUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json'
    }));
  }

  getStream<T>(path: string, params?: {}): Observable<T> {
    return this.get(this.streamUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  getStation<T>(path: string, params?: {}): Observable<T> {
    return this.get(this.stationUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  getSilenceLogs<T>(path: string, params?: {}): Observable<T> {
    return this.get(this.silenceLogsUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  getConfig<T>(path: string, params?: {}): Observable<T> {
    return this.get(this.configUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  getProfile<T>(path: string, params?: {}): Observable<T> {
    return this.get(this.profileUrl + path, params);
  }
  getIdsStationSIA<T>(path: string, params?: {}): Observable<T> {
    return this.get(this.streamUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }



  getDownload<T>(path: string,  params?: {}): Observable<T> {
    return this.get(this.downloadsUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  getAudioSubscriptions<T>(path: string,  params?: {}): Observable<T> {
    return this.get(this.audioSubscriptionsUrl + path, params, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  putProfile<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.put(this.profileUrl + path, body, headers);
  }

  postProfile<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.post(this.profileUrl + path, body, headers);
  }

  postConfig<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.post(this.configUrl + path, body, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  postDownload<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.post(this.downloadsUrl + path, body, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  postSubscription<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.post(this.subscriptionsUrl + path, body, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  postAudioSubscriptions<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.post(this.audioSubscriptionsUrl + path, body, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }



  postStream<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.post(this.streamUrl + path, body, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }


  putStream<T>(path: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    return this.put(this.streamUrl + path, body, headers);
  }

  deleteAudioSubscriptions<T>(path: string): Observable<T> {
    return this.delete(this.audioSubscriptionsUrl + path, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  deleteStream<T>(path: string): Observable<T> {
    return this.delete(this.streamUrl + path, new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + btoa(environment.apiToken + ':?')
    }));
  }

  private post<T>(url: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    const options = { headers };

    return this.restMethod(RestMethod.POST, url, options, body);
  }

  private put<T>(url: string, body: any, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    const options = { headers };

    return this.restMethod(RestMethod.PUT, url, options, body);
  }

  private get<T>(url: string, params?: {}, headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    const options = { headers, params: params ? params : null };

    return this.restMethod(RestMethod.GET, url, options);
  }

  private delete<T>(url: string,  headers: HttpHeaders = new HttpHeaders()): Observable<T> {
    const options = { headers };

    return this.restMethod(RestMethod.DELETE, url, options);
  }

  private restMethod<T>(restMethod: RestMethod, url: string, options: {}, body?: {}): Observable<T> {
    return new Observable(
      (observer) => {
        let request;

        switch (restMethod) {
          case RestMethod.GET:
            request = this.http.get(url, options); // .timeout(10000);
            break;
          case RestMethod.POST:
            request = this.http.post(url, body, options);
            break;
          case RestMethod.PUT:
            request = this.http.put(url, body, options);
            break;
          case RestMethod.DELETE:
            request = this.http.request('delete', url, options);
        }

        request.subscribe(
          (response) => {
            observer.next(response);
          },
          (err) => {
            console.error('Error on GET [' + url + '].', err);
            const errors = new RemoteErrors(err);
            observer.error(errors);
          },
          () => {
            observer.complete();
          }
        );
      }
    );
  }
}
